import React, { useContext, useRef } from "react";
import Button from "../Components/Button";
import ReportsLayout from "./ReportsLayout";
import useSalesSummary from "../hooks/useSalesSummary";
import { FilterContext } from "../FilterContextProvider";
import ReactToPrint from "react-to-print";
import Select from "react-select";
import useDateSelections from "../hooks/useDateSelections";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { exportToCSV } from "../Utility/Csv";

const Promo = () => {
  const { filterData } = useContext(FilterContext);
  const { productsToRender } = useSalesSummary("");
  const printRef = useRef();
  const { dateOptions, handleDateSelect, renderCustomDateSelector } =
    useDateSelections();

  const totalManagementComp = productsToRender.reduce((a, b) => {
    return a + b.management_comp_cost;
  }, 0);

  const totalMailOrder = productsToRender.reduce((a, b) => {
    return a + b.mail_order_cost;
  }, 0);

  const totalBandComp = productsToRender.reduce((a, b) => {
    return a + b.band_comp_quantity_cost;
  }, 0);

  const totalDamage = productsToRender.reduce((a, b) => {
    return a + b.damage_quantity_cost;
  }, 0);

  const prodsToExport = productsToRender
    .filter(
      (prod) =>
        prod.sold !== undefined &&
        (prod.management_comp !== 0 ||
          prod.mail_order !== 0 ||
          prod.band_comp_quantity !== 0 ||
          prod.damage_quantity !== 0)
    )
    .map((prod) => ({
      Name: prod.name,
      Size: prod.product_size,
      Average_cost:
        (prod.management_comp_cost +
          prod.mail_order_cost +
          prod.band_comp_quantity_cost +
          prod.damage_quantity_cost) /
        (prod.management_comp +
          prod.mail_order +
          prod.band_comp_quantity +
          prod.damage_quantity),
      MGMT_comp_quantity: prod.management_comp,
      MGMT_comp_cost: prod.management_comp_cost,
      Mail_order_quantity: prod.mail_order,
      Mail_order_cost: prod.mail_order_cost,
      Artist_comp_quantity: prod.band_comp_quantity,
      Artist_comp_cost: prod.band_comp_quantity_cost,
      Damage_quantity: prod.damage_quantity,
      Damage_cost: prod.damage_quantity_cost,
    }));

  const totals = {
    Name: "Totals",
    Size: "",
    Average_cost: "",
    MGMT_comp_quantity: productsToRender.reduce((a, b) => {
      return a + parseFloat(b.management_comp);
    }, 0),
    MGMT_comp_cost: productsToRender.reduce((a, b) => {
      return a + parseFloat(b.management_comp_cost);
    }, 0),
    Mail_order_quantity: productsToRender.reduce((a, b) => {
      return a + parseFloat(b.mail_order);
    }, 0),
    Mail_order_cost: productsToRender.reduce((a, b) => {
      return a + parseFloat(b.mail_order_cost);
    }, 0),
    Band_comp_quantity: productsToRender.reduce((a, b) => {
      return a + parseFloat(b.band_comp_quantity);
    }, 0),
    Band_comp_cost: productsToRender.reduce((a, b) => {
      return a + parseFloat(b.band_comp_quantity_cost);
    }, 0),
    Damage_quantity: productsToRender.reduce((a, b) => {
      return a + parseFloat(b.damage_quantity);
    }, 0),
    Damage_cost: productsToRender.reduce((a, b) => {
      return a + parseFloat(b.damage_quantity_cost);
    }, 0),
  };

  const combined = prodsToExport.concat(totals);

  return (
    <ReportsLayout>
      <div className="mt-8 mx-8 py-10 px-12 bg-white rounded-lg border">
        {productsToRender.length > 0 && (
          <div className="flex justify-end text-sm">
            <ReactToPrint
              trigger={() => <Button className="button" text="Print"></Button>}
              content={() => printRef.current}
            />
            <div className="ml-2">
              <Button
                className="button button-white"
                text="Export"
                onClick={() =>
                  exportToCSV(combined, `${filterData.artist_name}_promo`)
                }
              ></Button>
            </div>
          </div>
        )}
        <div className="max-w-max">
          <Link to="/reports">
            <div className="-mt-6 mb-6 font-medium text-black text-sm">
              <FontAwesomeIcon icon={faArrowLeft}></FontAwesomeIcon> Back to
              reports
            </div>
          </Link>
        </div>
        <div className="flex flex-col text-sm w-1/4">
          <span className="text-xs">SELECT DATE RANGE</span>
          <Select
            id="date"
            className="text-xs font-light w-full"
            placeholder="Select date range"
            options={dateOptions}
            onChange={handleDateSelect}
            value={dateOptions?.filter(
              (each) => each.value === filterData.date_button
            )}
          />
        </div>
        <div className="my-4 w-1/2">
          {filterData.date_button === "custom" && renderCustomDateSelector()}
        </div>
        <div ref={printRef} className="mt-8 mx-8 text-black">
          <div className="flex text-lg font-bold mb-2">
            <div className="w-1/2">
              <div className="text-xl font-extrabold">TM Promo Report</div>
              <div>{filterData.artist_name}</div>
              <div className="flex">
                <div className="text-base font-normal italic">
                  <div>Start Date:</div>
                  <div>End Date:</div>
                </div>
                <div className="ml-2 text-base font-normal italic">
                  <div>
                    {filterData.start_date?.slice(5, 7)}/
                    {filterData.start_date?.slice(8, 10)}/
                    {filterData.start_date?.slice(0, 4)}
                  </div>
                  <div>
                    {filterData.end_date?.slice(5, 7)}/
                    {filterData.end_date?.slice(8, 10)}/
                    {filterData.end_date?.slice(0, 4)}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex text-base font-normal text-right">
              <div>
                <div>Management Total:</div>
                <div>Mail Order Total:</div>
                <div>Artist Total:</div>
                <div>Damage Total:</div>
              </div>
              <div className="ml-2">
                <div>
                  {totalManagementComp !== 0
                    ? totalManagementComp?.toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                      })
                    : 0}
                </div>
                <div>
                  {totalMailOrder !== 0
                    ? totalMailOrder?.toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                      })
                    : 0}
                </div>
                <div>
                  {totalBandComp !== 0
                    ? totalBandComp?.toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                      })
                    : 0}
                </div>
                <div>
                  {totalDamage !== 0
                    ? totalDamage?.toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                      })
                    : 0}
                </div>
              </div>
            </div>
          </div>
          <table className="w-full">
            <thead className="text-xs font-light">
              <tr className="h-12 text-right">
                <td className="w-1/4 text-left">PRODUCT</td>
                <td className="w-thinner text-left">SIZE</td>
                <td className="w-1/12">AVERAGE COST</td>
                <td className="w-thinner"></td>
                <td className="w-1/12" colSpan="2">
                  MGMT COMP
                </td>
                <td className="w-thinner"></td>
                <td className="w-1/12" colSpan="2">
                  MAIL ORDER
                </td>
                <td className="w-thinner"></td>
                <td className="w-1/12" colSpan="2">
                  ARTIST COMP
                </td>
                <td className="w-thinner"></td>
                <td className="w-1/12" colSpan="2">
                  DAMAGE
                </td>
              </tr>
            </thead>
            <tbody>
              {productsToRender
                .filter(
                  (prod) =>
                    prod.sold !== undefined &&
                    (prod.management_comp !== 0 ||
                      prod.mail_order !== 0 ||
                      prod.band_comp_quantity !== 0 ||
                      prod.damage_quantity !== 0)
                )
                .map((productToRender, i) => {
                  return (
                    <tr
                      key={productToRender.id}
                      className="text-sm text-right h-7"
                    >
                      <td id={`product_name_${i}`} className="text-left">
                        {productToRender.name}
                      </td>
                      <td id={`size_${i}`} className="text-left">
                        {productToRender.product_size}
                      </td>
                      <td id={`cost_${i}`}>
                        {(
                          (productToRender.management_comp_cost +
                            productToRender.mail_order_cost +
                            productToRender.band_comp_quantity_cost +
                            productToRender.damage_quantity_cost) /
                          (productToRender.management_comp +
                            productToRender.mail_order +
                            productToRender.band_comp_quantity +
                            productToRender.damage_quantity)
                        )?.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                        })}
                      </td>
                      <td></td>
                      <td id={`management_comp_${i}`}>
                        {productToRender.management_comp}
                      </td>
                      <td id={`management_comp_cost_${i}`}>
                        {productToRender.management_comp !== 0
                          ? productToRender.management_comp_cost?.toLocaleString(
                              "en-US",
                              {
                                style: "currency",
                                currency: "USD",
                              }
                            )
                          : 0}
                      </td>
                      <td></td>
                      <td id={`mail_order_${i}`}>
                        {productToRender.mail_order}
                      </td>
                      <td id={`mail_order_cost_${i}`}>
                        {productToRender.mail_order !== 0
                          ? productToRender.mail_order_cost?.toLocaleString(
                              "en-US",
                              {
                                style: "currency",
                                currency: "USD",
                              }
                            )
                          : 0}
                      </td>
                      <td></td>
                      <td id={`band_comp_quantity_${i}`}>
                        {productToRender.band_comp_quantity}
                      </td>
                      <td id={`band_comp_quantity_cost_${i}`}>
                        {productToRender.band_comp_quantity !== 0
                          ? productToRender.band_comp_quantity_cost?.toLocaleString(
                              "en-US",
                              {
                                style: "currency",
                                currency: "USD",
                              }
                            )
                          : 0}
                      </td>
                      <td></td>
                      <td id={`damage_quantity_${i}`}>
                        {productToRender.damage_quantity}
                      </td>
                      <td id={`damage_quantity_cost_${i}`}>
                        {productToRender.damage_quantity !== 0
                          ? productToRender.damage_quantity_cost?.toLocaleString(
                              "en-US",
                              {
                                style: "currency",
                                currency: "USD",
                              }
                            )
                          : 0}
                      </td>
                    </tr>
                  );
                })}
              <tr className="text-right h-12 text-sm">
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td colSpan="2">
                  {totalManagementComp !== 0
                    ? totalManagementComp?.toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                      })
                    : 0}
                </td>
                <td></td>
                <td colSpan="2">
                  {totalMailOrder !== 0
                    ? totalMailOrder?.toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                      })
                    : 0}
                </td>
                <td></td>
                <td colSpan="2">
                  {totalBandComp !== 0
                    ? totalBandComp?.toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                      })
                    : 0}
                </td>
                <td></td>
                <td colSpan="2">
                  {totalDamage !== 0
                    ? totalDamage?.toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                      })
                    : 0}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </ReportsLayout>
  );
};

export default Promo;
